import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

interface TenancyData {
  brandPrincipal: { id: string; name: string; slug: string; archetype: string; defaultCurrency: string; timezone: string };
  brands: { id: string; name: string; brandCode: string }[];
  distributorGroup: { id: string; name: string } | null;
  distributorOrgs: { id: string; name: string; tinNumber: string | null }[];
  brandUsers: { id: string; name: string; email: string; role: string }[];
  distributorUsers: { id: string; name: string; email: string; role: string; distributorOrgName: string }[];
}

export function TenancyCheckPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<TenancyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<TenancyData>('/demo/tenancy')
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 max-w-[940px]">
      <div>
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2">
          {t('tenancy.devLabel')}
        </span>
        <h1 className="font-serif italic text-3xl text-ink">
          {t('tenancy.title')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t('tenancy.subtitle')}</p>
      </div>

      {loading && (
        <p className="text-slate-500 text-sm font-mono">{t('tenancy.loading')}</p>
      )}

      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded p-4 text-danger text-sm font-mono">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-6">
          <Section title={t('tenancy.brandPrincipal')}>
            <Row label="Name" value={data.brandPrincipal.name} />
            <Row label="Slug" value={data.brandPrincipal.slug} mono />
            <Row label="Currency" value={data.brandPrincipal.defaultCurrency} mono />
            <Row label="Timezone" value={data.brandPrincipal.timezone} mono />
            <Row label="ID" value={data.brandPrincipal.id} mono dim />
          </Section>

          <Section title={`${t('tenancy.brands')} (${data.brands.length})`}>
            {data.brands.map((b) => (
              <Row key={b.id} label={b.brandCode} value={b.name} mono />
            ))}
          </Section>

          <Section title={`${t('tenancy.distributorOrgs')} (${data.distributorOrgs.length})`}>
            {data.distributorOrgs.map((o) => (
              <div key={o.id} className="py-1 border-b border-hairline last:border-0">
                <div className="font-medium text-sm text-ink">{o.name}</div>
                <div className="font-mono text-xs text-slate-500">{o.tinNumber}</div>
              </div>
            ))}
          </Section>

          <Section title={`${t('tenancy.brandUsers')} (${data.brandUsers.length})`}>
            {data.brandUsers.map((u) => (
              <div key={u.id} className="py-1 border-b border-hairline last:border-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-ink">{u.name}</span>
                  <span className="text-xs font-mono text-copper-deep bg-copper-soft px-2 py-0.5 rounded">
                    {u.role}
                  </span>
                </div>
                <div className="font-mono text-xs text-slate-500">{u.email}</div>
                <div className="font-mono text-xs text-slate-300">{u.id}</div>
              </div>
            ))}
          </Section>

          <Section title={`${t('tenancy.distributorUsers')} (${data.distributorUsers.length})`}>
            {data.distributorUsers.map((u) => (
              <div key={u.id} className="py-1 border-b border-hairline last:border-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-ink">{u.name}</span>
                  <span className="text-xs text-slate-500">{u.distributorOrgName}</span>
                </div>
                <div className="font-mono text-xs text-slate-500">{u.email}</div>
                <div className="font-mono text-xs text-slate-300">{u.id}</div>
              </div>
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-bone border border-hairline rounded-xl p-6">
      <h2 className="font-serif italic text-lg text-ink mb-4">{title}</h2>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, dim }: { label: string; value: string; mono?: boolean; dim?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-hairline last:border-0">
      <span className="text-xs text-slate-500 font-medium">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${dim ? 'text-slate-400' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  );
}
